import { v4 as uuidv4 } from 'uuid';
import DevsiteCookies, { getCookie, setRootDomainCookie } from '../DevsiteCookies';

/** Legacy Mixpanel distinct_id. */
export const ANONYMOUS_DEVICE_ID_KEY = 'devsite_anonymous_device_id';

/** Maximum lifetime of a stored device ID before a new UUID is issued. */
export const ANONYMOUS_DEVICE_ID_TTL_MONTHS = 13;

type StoredAnonymousDeviceId = {
  id: string;
  createdAt: number;
};

/**
 * Returns the shared device id when metrics are permitted.
 * Reuses the existing cookie, copies a legacy localStorage id on the first permitted visit,
 * or assigns a new uuid.
 */
export const getOrCreateAnonymousDeviceId = (): string => {
  // If analytics id cookie already exists, return it
  const existingDeviceId = getCookie(DevsiteCookies.ANALYTICS_ID);
  if (existingDeviceId) {
    return existingDeviceId;
  }

  // If a legacy id in localStorage exists, switch it to be a cookie instead
  const legacyDeviceId = getLegacyAnonymousDeviceId();
  if (legacyDeviceId) {
    if (setDeviceIdCookie(legacyDeviceId.id, getExpiryDate(legacyDeviceId.createdAt))) destroyAnonymousDeviceId();
    return legacyDeviceId.id;
  }

  // If neither exist, create a new device id
  const newDeviceId = uuidv4();
  setDeviceIdCookie(newDeviceId, getExpiryDate(Date.now()));
  return newDeviceId;
};

const getExpiryDate = (createdAt: number): Date => {
  const expiresAt = new Date(createdAt);
  expiresAt.setMonth(expiresAt.getMonth() + ANONYMOUS_DEVICE_ID_TTL_MONTHS);
  return expiresAt;
};

const setDeviceIdCookie = (id: string, expiry: Date) => {
  try {
    setRootDomainCookie(DevsiteCookies.ANALYTICS_ID, id, expiry);
    return getCookie(DevsiteCookies.ANALYTICS_ID) === id;
  } catch {
    return false;
  }
};

/**
 * Returns the legacy deviceId if it exists and is not expired
 */
export const getLegacyAnonymousDeviceId = () => {
  try {
    const existing = localStorage.getItem(ANONYMOUS_DEVICE_ID_KEY);
    const parsed = parseStoredAnonymousDeviceId(existing);
    const isExpired = parsed && isAnonymousDeviceIdExpired(parsed.record.createdAt);
    // Remove the local storage item if it is expired
    if (isExpired) destroyAnonymousDeviceId();
    if (parsed && !isExpired) {
      return parsed.record;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

/**
 * @deprecated Use cookie-based methods instead
 */
const isStoredAnonymousDeviceId = (value: unknown): value is StoredAnonymousDeviceId => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const storedId = value as StoredAnonymousDeviceId;
  if (typeof storedId.id !== 'string' || storedId.id.length === 0) {
    return false;
  }
  if (!Number.isFinite(storedId.createdAt)) {
    return false;
  }
  return true;
};

/**
 * @deprecated Use cookie-based methods instead
 */
const parseStoredAnonymousDeviceId = (
  raw: string | null
): { record: StoredAnonymousDeviceId; migrate: boolean } | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredAnonymousDeviceId(parsed)) return null;
    return { record: parsed, migrate: false };
  } catch {
    return { record: { id: raw, createdAt: Date.now() }, migrate: true };
  }
};

/**
 * @deprecated Use cookie-based methods instead
 */
const isAnonymousDeviceIdExpired = (createdAt: number, now: number = Date.now()): boolean => {
  const expiresAt = new Date(createdAt);
  // Add the TTL to the createdAt timestamp to get the expiresAt timestamp.
  expiresAt.setMonth(expiresAt.getMonth() + ANONYMOUS_DEVICE_ID_TTL_MONTHS);
  return now >= expiresAt.getTime();
};

/**
 * Destroys the anonymous device ID by removing it from localStorage and resetting the in-memory ID.
 * Ensures that the anonymous device ID is purged from both memory and storage (if applicable).
 */
export const destroyAnonymousDeviceId = (): void => {
  try {
    localStorage.removeItem(ANONYMOUS_DEVICE_ID_KEY);
  } catch {
    // storage unavailable (e.g. blocked browsers)
  }
};
